import type { AssetLoadingType } from "replo-runtime/shared/asset-loading";
import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

import * as React from "react";

import Popover from "@v1/components/designSystem/popover/Popover";
import AssetPicker from "@v1/components/editor/AssetPicker";
import { BADGE_TRIGGER_OFFSET } from "@v1/components/editor/constants";
import DynamicDataValueIndicator from "@v1/components/editor/page/element-editor/components/extras/DynamicDataValueIndicator";
import InlineAssetSelector from "@v1/components/editor/page/element-editor/components/modifiers/InlineAssetSelector";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import { useAreModalsOpen } from "@v2/store/modalStore";
import { isDynamicDataValue } from "replo-runtime/shared/utils/dynamic-data";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

import { DynamicDataSelector } from "../../../DynamicDataSelector";

type VideoSourceSelectorProps = {
  src: any;
  thumbnailSrc?: string | ReploMixedStyleValue;
  assetLoadingValue: AssetLoadingType | ReploMixedStyleValue;
  objectFitValue?: any;
  objectPositionValues?: {
    x?: string | ReploMixedStyleValue;
    y?: string | ReploMixedStyleValue;
  };
  onChangeObjectFit?(value: string): void;
  onChangeObjectPositionX?(value: string): void;
  onChangeObjectPositionY?(value: string): void;
  onChangeAssetLoading?(value: AssetLoadingType): void;
  onChangePoster?(value: string | null): void;
  onChangeSource(value: string): void;
  onRemove?(): void;
  componentId: string | undefined;
};

const VideoSourceSelector = ({
  src,
  objectFitValue,
  objectPositionValues,
  onChangeObjectFit,
  onChangeObjectPositionX,
  onChangeObjectPositionY,
  onChangeSource,
  onRemove,
  componentId,
  thumbnailSrc,
  onChangeAssetLoading,
  onChangePoster,
  assetLoadingValue,
}: VideoSourceSelectorProps) => {
  const [isVisible, setVisible] = React.useState<boolean>();
  const areModalsOpen = useAreModalsOpen();
  const applyComponentAction = useApplyComponentAction();

  const onClickSelectAsset = () => {
    if (
      Boolean(onChangeObjectFit) ||
      Boolean(onChangeObjectPositionX) ||
      Boolean(onChangeObjectPositionY)
    ) {
      setVisible(true);
    }
  };

  const handlePosterChange = React.useCallback(
    (poster: string) => {
      applyComponentAction({
        type: "setProps",
        value: { poster },
      });
    },
    [applyComponentAction],
  );

  return (
    <div className="flex flex-grow flex-col">
      <Popover.Root isOpen={isVisible} onOpenChange={setVisible}>
        {isDynamicDataValue(src) ? (
          <DynamicDataSelector
            trigger={
              <DynamicDataValueIndicator
                type="image"
                templateValue={src}
                onRemove={() => onRemove?.()}
                componentId={componentId}
              />
            }
            targetType="text/url"
            onChange={onChangeSource}
            onRemove={() => onRemove?.()}
            location="designPanel"
          />
        ) : (
          <InlineAssetSelector
            size="sm"
            emptyTitle="Select Video"
            inputPlaceholder="Video URL"
            allowsDynamicData
            onClickSelectAsset={() => onClickSelectAsset?.()}
            onRemoveAsset={() => onRemove?.()}
            allowRemoveAsset={Boolean(onRemove)}
            shouldOpenLibraryPopover={false}
            asset={
              isMixedStyleValue(src)
                ? src
                : {
                    type: "video",
                    src: src,
                  }
            }
            onChange={(url) => {
              return applyComponentAction({
                type: "setProps",
                value: { url },
              });
            }}
          />
        )}
        <Popover.Anchor className="relative left-0 top-0" />
        {isVisible && (
          <Popover.Content
            title="Video"
            shouldPreventDefaultOnInteractOutside={areModalsOpen}
            sideOffset={BADGE_TRIGGER_OFFSET}
          >
            <AssetPicker
              objectFitValue={objectFitValue}
              objectPositionValue={objectPositionValues}
              assetType="video"
              emptyTitle="No Video Selected"
              selectedStateButtonLabel="Select Video"
              emptyStateButtonLabel="Change Video"
              url={src}
              onChangeObjectFit={onChangeObjectFit}
              onChangeObjectPositionX={onChangeObjectPositionX}
              onChangeObjectPositionY={onChangeObjectPositionY}
              onChangeSource={onChangeSource}
              assetLoadingValue={assetLoadingValue}
              onChangeAssetLoading={onChangeAssetLoading}
              onChangeAssetLoadingPoster={onChangePoster}
              onPosterInputChange={handlePosterChange}
              onRemoveThumbnail={() => {
                onChangePoster?.(null);
              }}
              thumbnailSrc={thumbnailSrc}
              componentId={componentId}
            />
          </Popover.Content>
        )}
      </Popover.Root>
    </div>
  );
};

export default VideoSourceSelector;
